import LabelWithPopover from "@/components/form/labels/LabelWithPopover";
import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import { InputNumber } from "@/components/InputNumber";
import {
  datePickerClearableProps,
  datePickerFormItemProps,
  datePickerStandardProps,
  selectStandardProps,
} from "@/utils/componentUtils";
import { formatLocaleCurrency } from "@/utils/formatUtils";
import { selectFilterFunction } from "@/utils/formUtils";
import { toDate } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { Card, Checkbox, Col, DatePicker, Descriptions, Divider, Form, Row, Select } from "antd";
import dayjs from "dayjs";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { rowGutter } from "../../../../../../common/constants";
import {
  TravelClientDiscountType,
  TravelDestination,
  TravelInsuranceType,
  TravelLuggageInsuranceAmount,
  TravelRiskGroup,
  TravelTerritorialValidity,
} from "../../enums";
import { TravelCalc } from "../../types";

const colSpan = 4;
const beginningDateFrom = dayjs();
const beginningDateTo = dayjs().add(18, "month");
const firstPaymentDateFrom = dayjs().subtract(1, "years");
const firstPaymentDateTo = dayjs();

enum FamilyInsuranceInstitutions {
  AXA = "AXA",
  UNION = "UNION",
  GENERALI = "GENERALI",
}

export const TravelCalcForm = () => {
  const { t } = useTranslation();

  const form = Form.useFormInstance<TravelCalc>();

  const insuranceType = Form.useWatch(["generalData", "insuranceType"], form);
  const isShortTermInsuranceType = insuranceType === TravelInsuranceType.SHORT_TERM;
  const isYearInsuranceType = insuranceType === TravelInsuranceType.YEAR;
  const isCancellationInsuranceType = insuranceType === TravelInsuranceType.CANCELLATION;
  const effectiveBeginningDate = Form.useWatch(["generalData", "effectiveBeginningDate"], form);
  const studyStay = Form.useWatch(["generalData", "studyStay"], form);
  const endDateFrom = effectiveBeginningDate ? effectiveBeginningDate : dayjs();
  const endDateTo = effectiveBeginningDate ? dayjs(effectiveBeginningDate).add(1, "year") : dayjs().add(1, "year");

  const manualWorkSelected = Form.useWatch(["generalData", "riskGroup"], form) === TravelRiskGroup.MANUAL_WORK;
  const insuredClientsCount = Form.useWatch(["clientsData", "insuredClientsCount"], form) || 0;
  const showLuggageAmount = Form.useWatch(["coveragesData", "luggage"], form) && isShortTermInsuranceType;
  const coverageDataCancellation = Form.useWatch(["coveragesData", "cancellation"], form);

  const destinationOptions = useMemo(
    () =>
      Object.keys(TravelDestination).map((destination) => ({
        value: destination,
        label: t("calc.travel.enums.destination." + destination),
      })),
    [t],
  );

  const handleRiskGroupChange = (riskGroup: TravelRiskGroup) => {
    if (riskGroup === TravelRiskGroup.MANUAL_WORK) {
      form.setFieldsValue({ discountsData: { unionHealthInsuranceClient: false } });
    }
  };

  const handleInsuredClientBirthDateChange = () => {
    form.validateFields([["discountsData"]]);
  };

  return (
    <>
      <Card type="inner" className="card-box" title={t("calc.travel.sections.generalData")}>
        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item
              name={["generalData", "effectiveBeginningDate"]}
              label={t("calc.travel.attrs.generalData.effectiveBeginningDate")}
              rules={[validations.notNull, validations.dateInInterval(beginningDateFrom, beginningDateTo)]}
              {...datePickerFormItemProps}
            >
              <DatePicker {...datePickerStandardProps} minDate={beginningDateFrom} maxDate={beginningDateTo} />
            </Form.Item>
          </Col>

          {isShortTermInsuranceType || isCancellationInsuranceType ? (
            <Col span={colSpan}>
              <Form.Item
                name={["generalData", "effectiveEndDate"]}
                label={t("calc.travel.attrs.generalData.effectiveEndDate")}
                dependencies={[["generalData", "effectiveBeginningDate"]]}
                rules={[validations.notNull, validations.dateInInterval(endDateFrom, endDateTo)]}
                {...datePickerFormItemProps}
              >
                <DatePicker
                  {...datePickerStandardProps}
                  showNow={false}
                  minDate={toDate(endDateFrom)}
                  maxDate={endDateTo}
                />
              </Form.Item>
            </Col>
          ) : undefined}

          {isShortTermInsuranceType ? (
            <Col span={colSpan}>
              <Form.Item
                name={["generalData", "destination"]}
                label={t("calc.travel.enums.destination._label")}
                rules={[validations.notNull]}
              >
                <Select
                  {...selectStandardProps}
                  showSearch={{
                    filterOption: (input, option) =>
                      selectFilterFunction(input, option) ||
                      String(option?.value).toLowerCase().startsWith(input.toLowerCase()),
                  }}
                  options={destinationOptions}
                />
              </Form.Item>
            </Col>
          ) : (
            <Col span={colSpan}>
              <Form.Item
                name={["generalData", "territorialValidity"]}
                label={t("calc.travel.enums.territorialValidity._label")}
                rules={[validations.notNull]}
              >
                <Select
                  {...selectStandardProps}
                  options={Object.keys(TravelTerritorialValidity)
                    .filter((validity) => validity !== TravelTerritorialValidity.SVK)
                    .map((validity) => ({
                      value: validity,
                      label: t("calc.travel.enums.territorialValidity." + validity),
                    }))}
                />
              </Form.Item>
            </Col>
          )}

          {isShortTermInsuranceType || isYearInsuranceType ? (
            <Col span={colSpan}>
              <Form.Item
                name={["generalData", "riskGroup"]}
                label={
                  <LabelWithPopover
                    popoverTitle={t("calc.travel.helpers.riskGroupDesc.title")}
                    popoverContent={
                      <Descriptions
                        styles={{ root: { maxWidth: 550 }, label: { width: 150 } }}
                        size="small"
                        items={Object.values(TravelRiskGroup).map((group) => ({
                          label: t("calc.travel.enums.riskGroup." + group),
                          children: t("calc.travel.helpers.riskGroupDesc.enum." + group),
                        }))}
                        colon={false}
                        column={1}
                      />
                    }
                    label={t("calc.travel.enums.riskGroup._label")}
                  />
                }
                rules={[validations.notNull]}
              >
                <Select
                  {...selectStandardProps}
                  options={Object.keys(TravelRiskGroup).map((group) => ({
                    value: group,
                    label: t("calc.travel.enums.riskGroup." + group),
                  }))}
                  onChange={handleRiskGroupChange}
                  disabled={studyStay}
                />
              </Form.Item>
            </Col>
          ) : null}

          {(isShortTermInsuranceType || isYearInsuranceType) && (
            <Col span={colSpan}>
              <Form.Item
                name={["generalData", "studyStay"]}
                className="form-item-without-label"
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox
                  onChange={(e) => {
                    if (e.target.checked) {
                      form.setFieldValue(["generalData", "riskGroup"], TravelRiskGroup.TOURIST);
                      form.setFieldValue(["discountsData", "familyInsurance"], false);
                      form.setFieldValue(["clientsData", "insuredClientsAbroad"], false);
                    }
                  }}
                >
                  {t("calc.travel.attrs.generalData.studyStay")}
                </Checkbox>
              </Form.Item>
            </Col>
          )}
        </Row>

        {isCancellationInsuranceType && (
          <>
            <Divider className="divider-subheader">{t("calc.travel.sections.cancellationData")}</Divider>
            <Row gutter={rowGutter}>
              <Col span={5}>
                <Form.Item
                  name={["cancellationData", "tripPrice"]}
                  label={t("calc.travel.attrs.cancellationData.tripPrice")}
                  rules={[validations.notNull, validations.minNumber(100), validations.multipleOf100]}
                >
                  <InputNumber addon="euro" step={100} />
                </Form.Item>
              </Col>

              <Col span={5}>
                <Form.Item
                  name={["cancellationData", "firstPaymentDate"]}
                  label={t("calc.travel.attrs.cancellationData.firstPaymentDate")}
                  rules={[validations.notFuture, validations.dateInInterval(firstPaymentDateFrom, firstPaymentDateTo)]}
                  {...datePickerFormItemProps}
                >
                  <DatePicker
                    {...datePickerClearableProps}
                    minDate={firstPaymentDateFrom}
                    maxDate={firstPaymentDateTo}
                  />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}
      </Card>

      <Card type="inner" className="card-box" title={t("calc.travel.sections.insuredPersons")}>
        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item
              name={["clientsData", "insuredClientsCount"]}
              label={t("calc.travel.attrs.clientsData.insuredClientsCount")}
              rules={[validations.notNull, validations.minNumber(1), validations.maxNumber(99)]}
            >
              <InputNumber min={1} max={99} />
            </Form.Item>
          </Col>

          {isShortTermInsuranceType || isYearInsuranceType ? (
            <Col span={9} offset={11}>
              <Divider className="divider-subheader" style={{ margin: "0 0 9px 0" }}>
                {t("calc.travel.sections.additionalInfo")}
              </Divider>

              <Row gutter={rowGutter}>
                <Col span={10}>
                  <Form.Item
                    name={["discountsData", "familyInsurance"]}
                    valuePropName="checked"
                    rules={[validations.none]}
                    initialValue={false}
                  >
                    <Checkbox disabled={studyStay}>
                      <LabelWithPopover
                        label={t("calc.travel.attrs.discountsData.familyInsurance")}
                        popoverTitle={t("calc.travel.helpers.familyInsuranceDesc.title")}
                        popoverContent={
                          <Descriptions
                            styles={{ root: { maxWidth: 550 }, label: { width: 120 } }}
                            items={Object.values(FamilyInsuranceInstitutions).map((institution) => ({
                              label: t(`calc.travel.helpers.familyInsuranceDesc.enum.${institution}._label`),
                              children: t(`calc.travel.helpers.familyInsuranceDesc.enum.${institution}.content`),
                            }))}
                            colon={false}
                            column={1}
                          />
                        }
                      />
                    </Checkbox>
                  </Form.Item>
                </Col>

                {isYearInsuranceType && (
                  <Col span={14}>
                    <Form.Item
                      name={["discountsData", "unionHealthInsuranceClient"]}
                      valuePropName="checked"
                      rules={[validations.none]}
                      initialValue={false}
                    >
                      <Checkbox disabled={manualWorkSelected}>
                        <LabelWithTooltip
                          label={t("calc.travel.attrs.discountsData.unionHealthInsuranceClient")}
                          tooltip={t("calc.travel.helpers.unionHealthInsuranceClientDesc")}
                        />
                      </Checkbox>
                    </Form.Item>
                  </Col>
                )}

                {isYearInsuranceType && (
                  <Col span={24}>
                    <Form.Item
                      name={["clientsData", "insuredClientsAbroad"]}
                      valuePropName="checked"
                      rules={[validations.none]}
                      initialValue={false}
                    >
                      <Checkbox disabled={studyStay}>
                        {t("calc.travel.attrs.clientsData.insuredClientsAbroad")}
                      </Checkbox>
                    </Form.Item>
                  </Col>
                )}
              </Row>
            </Col>
          ) : undefined}
        </Row>

        <Row gutter={rowGutter}>
          {Array.from({ length: insuredClientsCount }, (_, i) => i + 1).map((clientNumber) => (
            <React.Fragment key={clientNumber}>
              <Col span={colSpan}>
                <Form.Item
                  name={["clientsData", "insuredClients", clientNumber - 1, "birthDate"]}
                  label={t("calc.travel.attrs.clientsData.insuredClients.birthDateLabel", { number: clientNumber })}
                  rules={[validations.notNull, validations.notPresentAndFuture]}
                  {...datePickerFormItemProps}
                >
                  <DatePicker
                    {...datePickerStandardProps}
                    showNow={false}
                    maxDate={dayjs().subtract(1, "day")}
                    onChange={handleInsuredClientBirthDateChange}
                  />
                </Form.Item>
              </Col>

              {isShortTermInsuranceType && (
                <>
                  <Col span={colSpan - 1}>
                    <Form.Item
                      name={["clientsData", "insuredClients", clientNumber - 1, "discountType"]}
                      label={t("calc.travel.enums.clientDiscountType._label")}
                      rules={[validations.none]}
                    >
                      <Select
                        {...selectStandardProps}
                        allowClear
                        options={Object.keys(TravelClientDiscountType).map((discount) => ({
                          value: discount,
                          label: t("calc.travel.enums.clientDiscountType." + discount),
                        }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={1} />
                </>
              )}
            </React.Fragment>
          ))}
        </Row>
      </Card>

      {(isShortTermInsuranceType || isYearInsuranceType) && (
        <Card type="inner" className="card-box" title={t("calc.travel.sections.coverages")}>
          <Row gutter={rowGutter}>
            <Col span={colSpan}>
              <Form.Item
                name={["coveragesData", "injury"]}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue
              >
                <Checkbox>{t("calc.travel.attrs.coveragesData.injury")}</Checkbox>
              </Form.Item>
            </Col>

            <Col span={colSpan * 2} offset={colSpan * 2}>
              <Form.Item
                name={["coveragesData", "airplaneTravel"]}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox>
                  <LabelWithTooltip
                    label={t("calc.travel.attrs.coveragesData.airplaneTravel")}
                    tooltip={t("calc.travel.helpers.coveragesData.airplaneTravelDesc")}
                  />
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={rowGutter}>
            <Col span={colSpan}>
              <Form.Item
                name={["coveragesData", "liability"]}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue
              >
                <Checkbox>{t("calc.travel.attrs.coveragesData.liability")}</Checkbox>
              </Form.Item>
            </Col>

            <Col span={colSpan * 2} offset={colSpan * 2}>
              <Form.Item
                name={["coveragesData", "vehicleRental"]}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox>
                  <LabelWithTooltip
                    label={t("calc.travel.attrs.coveragesData.vehicleRental")}
                    tooltip={t("calc.travel.helpers.coveragesData.vehicleRentalDesc")}
                  />
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={rowGutter}>
            <Col span={colSpan}>
              <Form.Item
                name={["coveragesData", "rescueService"]}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox>{t("calc.travel.attrs.coveragesData.rescueService")}</Checkbox>
              </Form.Item>
            </Col>

            <Col span={colSpan} offset={colSpan * 2}>
              <Form.Item
                name={["coveragesData", "pet"]}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox>
                  <LabelWithTooltip
                    label={t("calc.travel.attrs.coveragesData.pet")}
                    tooltip={t("calc.travel.helpers.coveragesData.petDesc")}
                  />
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={rowGutter}>
            <Col span={colSpan * 2}>
              <Form.Item
                name={["coveragesData", "emergencySituation"]}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox>
                  <LabelWithTooltip
                    label={t("calc.travel.attrs.coveragesData.emergencySituation")}
                    tooltip={t("calc.travel.helpers.coveragesData.emergencySituationDesc")}
                  />
                </Checkbox>
              </Form.Item>
            </Col>

            <Col span={colSpan * 2} offset={colSpan}>
              <Form.Item
                name={["coveragesData", "drinkAllowed"]}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox>
                  <LabelWithTooltip
                    label={t("calc.travel.attrs.coveragesData.drinkAllowed")}
                    tooltip={t("calc.travel.helpers.coveragesData.drinkAllowedDesc")}
                  />
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={rowGutter}>
            <Col span={colSpan * 2}>
              <Form.Item
                name={["coveragesData", "vehicleAssistance"]}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox>
                  <LabelWithTooltip
                    label={t("calc.travel.attrs.coveragesData.vehicleAssistance")}
                    tooltip={t("calc.travel.helpers.coveragesData.vehicleAssistanceDesc")}
                  />
                </Checkbox>
              </Form.Item>
            </Col>

            {isShortTermInsuranceType && (
              <Col span={colSpan * 2} offset={colSpan}>
                <Form.Item
                  name={["coveragesData", "abandonedHousehold"]}
                  valuePropName="checked"
                  rules={[validations.none]}
                  initialValue={false}
                >
                  <Checkbox>
                    <LabelWithTooltip
                      label={t("calc.travel.attrs.coveragesData.abandonedHousehold")}
                      tooltip={t("calc.travel.helpers.coveragesData.abandonedHouseholdDesc")}
                    />
                  </Checkbox>
                </Form.Item>
              </Col>
            )}

            {isYearInsuranceType && (
              <Col span={colSpan * 2} offset={colSpan}>
                <Form.Item
                  name={["coveragesData", "bicycleAssistance"]}
                  valuePropName="checked"
                  rules={[validations.none]}
                  initialValue={false}
                >
                  <Checkbox>
                    <LabelWithTooltip
                      label={t("calc.travel.attrs.coveragesData.bicycleAssistance")}
                      tooltip={t("calc.travel.helpers.coveragesData.bicycleAssistanceDesc")}
                    />
                  </Checkbox>
                </Form.Item>
              </Col>
            )}
          </Row>

          <Row gutter={rowGutter}>
            <Col span={colSpan}>
              <Form.Item
                name={["coveragesData", "cancellation"]}
                className={coverageDataCancellation ? "form-item-without-label" : undefined}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox>{t("calc.travel.attrs.coveragesData.cancellation")}</Checkbox>
              </Form.Item>
            </Col>

            <Col span={colSpan * 2}>
              {coverageDataCancellation && (
                <Row gutter={rowGutter}>
                  <Col span={12}>
                    <Form.Item
                      name={["coveragesData", "cancellationData", "tripPrice"]}
                      label={t("calc.travel.attrs.coveragesData.cancellationData.tripPrice")}
                      rules={[
                        ...(isShortTermInsuranceType
                          ? [validations.notNull, validations.minNumber(100), validations.multipleOf100]
                          : [validations.none]),
                      ]}
                    >
                      {isShortTermInsuranceType && <InputNumber addon="euro" step={100} />}

                      {isYearInsuranceType && (
                        <Select
                          {...selectStandardProps}
                          options={[3_000, 6_000, 10_000, 20_000].map((value) => ({
                            value: value,
                            label: formatLocaleCurrency(value, 0),
                          }))}
                        />
                      )}
                    </Form.Item>
                  </Col>

                  {isShortTermInsuranceType && (
                    <>
                      <Col span={12}>
                        <Form.Item
                          name={["coveragesData", "cancellationData", "complicity"]}
                          valuePropName="checked"
                          className="form-item-without-label"
                          rules={[validations.none]}
                          initialValue={true}
                        >
                          <Checkbox>{t("calc.travel.attrs.coveragesData.cancellationData.complicity")}</Checkbox>
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item
                          name={["coveragesData", "cancellationData", "firstPaymentDate"]}
                          label={t("calc.travel.attrs.coveragesData.cancellationData.firstPaymentDate")}
                          rules={[
                            validations.notFuture,
                            validations.dateInInterval(firstPaymentDateFrom, firstPaymentDateTo),
                          ]}
                          {...datePickerFormItemProps}
                        >
                          <DatePicker
                            {...datePickerClearableProps}
                            minDate={firstPaymentDateFrom}
                            maxDate={firstPaymentDateTo}
                          />
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item
                          name={["coveragesData", "cancellationData", "extendedCoverage"]}
                          valuePropName="checked"
                          className="form-item-without-label"
                          rules={[validations.none]}
                          initialValue={false}
                        >
                          <Checkbox style={{ maxHeight: 22 }}>
                            {t("calc.travel.attrs.coveragesData.cancellationData.extendedCoverage")}
                          </Checkbox>
                        </Form.Item>
                      </Col>
                    </>
                  )}

                  {isYearInsuranceType && (
                    <Col span={12}>
                      <Form.Item
                        name={["coveragesData", "cancellationData", "extendedCoverage"]}
                        valuePropName="checked"
                        className="form-item-without-label"
                        rules={[validations.none]}
                        initialValue={false}
                      >
                        <Checkbox>
                          <LabelWithTooltip
                            label={t("calc.travel.attrs.coveragesData.cancellationData.extendedCoverageYear")}
                            tooltip={t("calc.travel.helpers.coveragesData.cancellationExtraDesc")}
                          />
                        </Checkbox>
                      </Form.Item>
                    </Col>
                  )}
                </Row>
              )}
            </Col>

            <Col span={colSpan}>
              <Form.Item
                name={["coveragesData", "electronicDevices"]}
                className={coverageDataCancellation ? "form-item-without-label" : undefined}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox>
                  <LabelWithTooltip
                    tooltip={t("calc.travel.helpers.coveragesData.electronicDevicesDesc")}
                    label={t("calc.travel.attrs.coveragesData.electronicDevices")}
                  />
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={rowGutter}>
            <Col span={colSpan}>
              <Form.Item
                name={["coveragesData", "luggage"]}
                className={showLuggageAmount ? "form-item-without-label" : undefined}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox>{t("calc.travel.attrs.coveragesData.luggage")}</Checkbox>
              </Form.Item>
            </Col>

            {showLuggageAmount && (
              <Col span={colSpan}>
                <Form.Item
                  name={["coveragesData", "luggageInsuranceAmount"]}
                  label={t("calc.travel.enums.luggageInsuranceAmount._label")}
                  rules={[validations.notNull]}
                >
                  <Select
                    {...selectStandardProps}
                    options={Object.keys(TravelLuggageInsuranceAmount).map((amount) => ({
                      value: amount,
                      label: t("calc.travel.enums.luggageInsuranceAmount." + amount),
                    }))}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Card>
      )}
    </>
  );
};
